import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkCurrentSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        console.log("Session found, checking admin status...");
        const { data: adminData, error: adminError } = await supabase
          .from('admin_profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .single();

        console.log("Admin profile check result:", { adminData, adminError });

        if (!adminError && adminData?.is_admin) {
          console.log("Valid admin profile found, redirecting to admin panel");
          navigate('/admin');
        }
      }
    };

    checkCurrentSession();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("Attempting login...");
      const { data: { session }, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        console.error("Authentication error:", authError);
        throw authError;
      }

      if (!session) {
        console.error("No session found after login");
        throw new Error("Aucune session trouvée après la connexion");
      }

      console.log("Login successful, checking admin status...");
      const { data: adminData, error: adminError } = await supabase
        .from('admin_profiles')
        .select('is_admin')
        .eq('id', session.user.id)
        .single();

      console.log("Admin profile check result:", { adminData, adminError });

      if (adminError) {
        console.error("Admin profile check error:", adminError);
        throw new Error("Erreur lors de la vérification des droits administrateur");
      }

      if (!adminData?.is_admin) {
        console.error("User is not an admin");
        await supabase.auth.signOut();
        throw new Error("Accès non autorisé - Compte non administrateur");
      }

      console.log("Admin access confirmed, proceeding to admin panel");
      toast({
        title: "Connexion réussie",
        description: "Bienvenue dans l'interface d'administration",
      });

      navigate('/admin');
    } catch (error) {
      console.error("Login process error:", error);
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: error instanceof Error ? error.message : "Une erreur est survenue lors de la connexion",
      });
      
      // Ensure we're signed out if there was an error
      await supabase.auth.signOut();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-magazine-red p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Administration</CardTitle>
          <CardDescription>
            Connectez-vous à votre compte administrateur
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-magazine-red hover:bg-magazine-red/90"
              disabled={loading}
            >
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;